import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag-and-drop kanban for the publishing queue.
// Cards can be dragged between status columns. Valid drop targets are
// highlighted; invalid ones are dimmed. On drop, a form is submitted
// via Turbo to transition the item's status server-side.
export default class extends Controller {
  static targets = ["column", "form"]
  static values = {
    csrfToken: String,
    // Viewers (no content_edit) get the controller for `cardClicked`
    // — they can still click into a card to leave a comment — but
    // SortableJS stays off. The transition endpoint would reject their
    // drag anyway, and the resulting redirect made the card flash into
    // the target column for a moment before snapping back.
    dragEnabled: { type: Boolean, default: true },
    // Opt-in to "all-columns-minus-current" auto-refresh of each card's
    // data-allowed-targets after a cross-column drop. Safe for kanbans
    // where allowed transitions are uniformly "any other column"
    // (tasks). NOT safe for the publishing queue, which gates each
    // transition by role permissions — defaulting off preserves the
    // server-rendered allowed list there.
    autoRefreshAllowedTargets: { type: Boolean, default: false }
  }

  connect() {
    this.dragged = false
    // Submit lock: SortableJS can fire onEnd more than once for a single
    // user gesture (rapid release, double-click on the card before the
    // transition POST returns). Without this, the form posts twice and
    // the card "snaps back" — which is what QA reported as "drag for
    // end position é um bug".
    this.submitting = false
    this.sortables = []
    if (!this.dragEnabledValue) return
    this.columnTargets.forEach(col => {
      const sortable = Sortable.create(col.querySelector("[data-kanban-items]"), {
        group: "kanban",
        animation: 200,
        ghostClass: "ford-kanban-ghost",
        dragClass: "ford-kanban-drag",
        handle: "[data-kanban-card]",
        draggable: "[data-kanban-card]",
        // Be more forgiving on swap detection: default 1 means the card
        // has to cover an existing item by 100% before a swap registers,
        // which is jittery at column ends. 0.65 = 65% overlap is plenty.
        swapThreshold: 0.65,
        // Pixels of slack at the bottom of the container that still
        // register as "drop after last". With the column-fill CSS this
        // makes drops at the END always land in the items list instead
        // of bouncing back.
        emptyInsertThreshold: 24,

        onStart: (evt) => this.onDragStart(evt),
        onEnd: (evt) => this.onDragEnd(evt),
        onMove: (evt) => this.onMove(evt),
      })
      this.sortables.push(sortable)
    })
  }

  // Whole card is clickable — navigate to its edit/detail URL.
  // Skipped if the user just finished a drag, or if the click originated
  // from an interactive element (button, link, form input) inside the card.
  cardClicked(event) {
    if (this.dragged) return
    if (event.target.closest("a, button, input, [data-no-navigate]")) return
    const card = event.currentTarget
    const url = card.dataset.cardUrl
    if (!url) return
    if (window.Turbo) {
      window.Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  disconnect() {
    this.sortables.forEach(s => s.destroy())
    this.sortables = []
  }

  onDragStart(evt) {
    this.dragged = true
    const card = evt.item
    const allowed = (card.dataset.allowedTargets || "").split(",").filter(Boolean)
    // The source column is also a valid drop target — in-column
    // reorders are allowed. Without this it'd get the "drop-invalid"
    // class, which carries `pointer-events: none` and would block
    // SortableJS from detecting the drag returning to its own column.
    const sourceStatus = card.closest("[data-status]")?.dataset.status

    this.columnTargets.forEach(col => {
      const colStatus = col.dataset.status
      const isValid = colStatus === sourceStatus || allowed.includes(colStatus)
      if (isValid) {
        col.classList.add("ford-kanban-drop-valid")
        col.classList.remove("ford-kanban-drop-invalid")
      } else {
        col.classList.add("ford-kanban-drop-invalid")
        col.classList.remove("ford-kanban-drop-valid")
      }
    })
  }

  onDragEnd(evt) {
    setTimeout(() => { this.dragged = false }, 50)
    this.columnTargets.forEach(col => {
      col.classList.remove("ford-kanban-drop-valid", "ford-kanban-drop-invalid")
    })

    // No-op drop: same column, same index. Covers (1) ESC during drag,
    // (2) release over an invalid column where SortableJS reverts to
    // source, and (3) the user manually dragging back to the original
    // position. Skipping here avoids a wasteful transition POST.
    if (evt.from === evt.to && evt.oldIndex === evt.newIndex) return

    const card = evt.item
    const sourceCol = evt.from?.closest("[data-status]")
    const targetCol = evt.to?.closest("[data-status]")
    if (!targetCol) return

    const targetStatus = targetCol.dataset.status
    const sourceStatus = sourceCol?.dataset.status

    // Cross-column moves still need the target to be in this card's
    // allowed transitions; same-column reorders bypass that check
    // (allowedTargets excludes the current status by construction).
    if (sourceStatus !== targetStatus) {
      const allowed = (card.dataset.allowedTargets || "").split(",").filter(Boolean)
      if (!allowed.includes(targetStatus)) return
    }

    // Submit lock — prevents the same drop from posting twice (a tiny
    // double-release of the mouse, or a Turbo navigation racing the
    // user's next click, can otherwise fire onEnd back-to-back).
    if (this.submitting) return
    this.submitting = true

    // Optimistic UI sync: SortableJS has already moved the card DOM
    // node, but the column COUNT badges + "no items" placeholders are
    // server-rendered and don't reflect the move. Patch both columns
    // inline so what the user sees matches the action they just took.
    // Only on cross-column moves — same-column drops don't change counts.
    if (sourceStatus !== targetStatus) {
      this.adjustColumnCount(sourceCol, -1)
      this.adjustColumnCount(targetCol, +1)
      // allowed-targets was baked at render time as "all statuses minus
      // THIS card's column". After moving columns, that list is stale —
      // it still excludes the original column instead of the new one, so
      // onMove rejects any attempt to drag the card BACK until a full
      // reload re-renders the card with the right exclusions. Opt-in
      // because the publishing queue uses a different (role-gated)
      // shape and shouldn't get the naïve refresh.
      if (this.autoRefreshAllowedTargetsValue) {
        this.refreshAllowedTargets(card, targetStatus)
      }
    }

    // Index of the dropped card inside its new column. The server
    // persists this so a manual page reload preserves the drop order
    // instead of falling back to the default priority/due-date sort.
    const newPosition = Array.from(
      targetCol.querySelectorAll("[data-kanban-card]")
    ).indexOf(card)

    // Submit transition via the hidden form.
    // The form action may contain __ID__ as a placeholder for per-item endpoints.
    const form = this.formTarget
    const origAction = form.dataset.originalAction || form.action
    if (!form.dataset.originalAction) form.dataset.originalAction = form.action
    form.action = origAction.replace("__ID__", card.dataset.itemId)

    const idsInput = form.querySelector("[name='item_ids[]']")
    if (idsInput) idsInput.value = card.dataset.itemId
    const typeInput = form.querySelector("[name='item_type']")
    if (typeInput) typeInput.value = card.dataset.itemType
    form.querySelector("[name='target_status']").value = targetStatus
    this.setFormField(form, "position", String(newPosition))

    // Server responds 204 (no Turbo navigation), so we release the
    // submit lock ourselves once the request settles. Without this the
    // next drag would be permanently blocked.
    form.addEventListener("turbo:submit-end", () => { this.submitting = false }, { once: true })

    form.requestSubmit()
  }

  // Sets a hidden form field by name, creating it if absent. Used to
  // attach the `position` index to the existing transition form without
  // having to bake it into the ERB.
  setFormField(form, name, value) {
    let input = form.querySelector(`[name='${name}']`)
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      form.appendChild(input)
    }
    input.value = value
  }

  // Re-derives the card's allowed drop targets from the currently
  // rendered columns, minus the column it now lives in. Without this,
  // the data-allowed-targets baked at server-render time keeps pointing
  // at the original column, which silently blocks any drag back home.
  refreshAllowedTargets(card, newStatus) {
    if (!card || !newStatus) return
    const allStatuses = this.columnTargets
      .map(col => col.dataset.status)
      .filter(Boolean)
    const next = allStatuses.filter(s => s !== newStatus).join(",")
    card.dataset.allowedTargets = next
  }

  // Increments / decrements the column's count badge and toggles its
  // "no items" empty-state message based on whether any card nodes
  // remain. The status badge sits in the column header and the empty
  // state is a non-draggable <p> inside `[data-kanban-items]`.
  adjustColumnCount(col, delta) {
    if (!col || !delta) return
    const badge = col.querySelector(".ford-status-badge")
    if (badge) {
      const current = parseInt(badge.textContent, 10)
      if (!Number.isNaN(current)) badge.textContent = Math.max(0, current + delta)
    }
    const items = col.querySelector("[data-kanban-items]")
    if (!items) return
    const cardCount = items.querySelectorAll("[data-kanban-card]").length
    // The empty-state is the only <p> direct child without data-kanban-card.
    const emptyState = items.querySelector(":scope > p")
    if (emptyState) {
      emptyState.style.display = cardCount > 0 ? "none" : ""
    }
  }

  onMove(evt) {
    const card = evt.dragged
    const targetCol = evt.to?.closest("[data-status]")
    if (!targetCol) return false

    // Same-column moves are always allowed — they're reorders within
    // a column, not transitions. The allowed-targets gate is only for
    // cross-column drops. Without this short-circuit the user can't
    // drag a card from 2nd → 3rd in its own status.
    if (evt.from === evt.to) return true

    const targetStatus = targetCol.dataset.status
    const allowed = (card.dataset.allowedTargets || "").split(",").filter(Boolean)
    return allowed.includes(targetStatus)
  }
}
